import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Pagination from 'react-bootstrap/Pagination';
import Slider from 'react-slick';

import ProductCard from './ProductCard';
import { getItems } from '../../actions/itemActions';
import './product-slider.css';

function slidesToShowFromWidth() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1400;
  if (w <= 576) return 2;   // mobile
  if (w <= 768) return 2;
  if (w <= 992) return 3;
  if (w <= 1200) return 4;
  return 6;                 // desktop: always 6
}

const ProductSlider = () => {
  const dispatch = useDispatch();
  const sliderRef = useRef(null);

  const tokenString = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const token = tokenString ? JSON.parse(tokenString) : null;
  const tokenReady = token && token.role && typeof token.role.id !== 'undefined';

  const { items = [] } = useSelector((state) => state.itemReducer);

  const [slidesToShow, setSlidesToShow] = useState(slidesToShowFromWidth());
  const slidesToScroll = slidesToShow;
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    dispatch(getItems('api/product/'));
  }, [dispatch]);

  useEffect(() => {
    const onResize = () => setSlidesToShow(slidesToShowFromWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / slidesToShow));

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: total > slidesToShow,
      speed: 500,
      swipe: true,
      touchThreshold: 10,
      slidesToShow,
      slidesToScroll,
      responsive: [
        { breakpoint: 1200, settings: { slidesToShow: 4, slidesToScroll: 4 } },
        { breakpoint: 992,  settings: { slidesToShow: 3, slidesToScroll: 3 } },
        { breakpoint: 768,  settings: { slidesToShow: 2, slidesToScroll: 2 } },
        { breakpoint: 576,  settings: { slidesToShow: 2, slidesToScroll: 2 } }
      ],
      beforeChange: (_cur, next) => setCurrentPage(Math.floor(next / slidesToShow)),
      initialSlide: 0,
    }),
    [total, slidesToShow, slidesToScroll]
  );

  const setSliderInstance = useCallback((inst) => {
    sliderRef.current = inst;
  }, []);

  const goToPage = useCallback((page) => {
    if (!sliderRef.current) return;
    sliderRef.current.slickGoTo(page * slidesToShow);
  }, [slidesToShow]);

  const prevPage = useCallback(() => {
    goToPage(currentPage - 1 < 0 ? totalPages - 1 : currentPage - 1);
  }, [currentPage, totalPages, goToPage]);

  const nextPage = useCallback(() => {
    goToPage(currentPage + 1 >= totalPages ? 0 : currentPage + 1);
  }, [currentPage, totalPages, goToPage]);

  const pagerIndices = useMemo(() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i);
    if (currentPage <= 2) return [0, 1, 2, 3, 4];
    if (currentPage >= totalPages - 3)
      return [totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1];
    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }, [currentPage, totalPages]);

  const showGenPrice = !(tokenReady && [1, 2, 3, 4].includes(token?.role?.id));

  return (
    <section className="product-slider-section">
      <div className="product-slider-title-bar full-bleed">
        <h2 className="product-slider-title">Featured Products</h2>
      </div>

      <Container fluid className="product-slider-container full-bleed">
        <div className="slider-wrap">
          <Slider ref={setSliderInstance} {...settings}>
            {items.map((item) => (
              <div key={item.id} className="slide-col">
                <ProductCard
                  item={item}
                  showGenPrice={showGenPrice}
                  onShop={() => goToPage(currentPage)}
                />
              </div>
            ))}
          </Slider>

          <div className="slider-controls">
            <ButtonGroup aria-label="Slider arrows" className="controls-arrows">
              <Button variant="light" className="nav-arrow" onClick={prevPage} aria-label="Previous">‹</Button>
              <Button variant="light" className="nav-arrow" onClick={nextPage} aria-label="Next">›</Button>
            </ButtonGroup>

            <Pagination className="pager">
              {pagerIndices.map((pageIdx) => (
                <Pagination.Item
                  key={pageIdx}
                  active={pageIdx === currentPage}
                  onClick={() => goToPage(pageIdx)}
                >
                  {pageIdx + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ProductSlider;
